'use client';

import React, { useEffect, useRef, useState } from 'react';
import Slick from 'react-slick';
import { classnames } from '@/utils/classnames/classnames';
import useResponsiveValue from '@/hooks/useResponsiveValue';
import Slide from './components/slide';
import 'slick-carousel/slick/slick.css';
import './styles/index.css';
import { ArrowProps, SliderProps } from './types';
import Arrow from './components/arrow';

const Slider = ({
  ref,
  children,
  slideWidth,
  slidesToShow = 1,
  infinite = false,
  swipeToSlide = true,
  spaceBetween = 10,
  arrows = true,
  arrowSize = 32,
  arrowStyles = {},
  arrowClassName = '',
  arrowIconClassName = '',
  arrowVariant = 'default',
  overlayDarkArrow = false,
  containerClassName = '',
  ...props
}: SliderProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const slidesCount = React.Children.count(children);

  // Focus-containment without breaking the slide animation. react-slick marks
  // inactive slides (and all `infinite` clones) `aria-hidden="true"`; their CTAs
  // would otherwise stay in the tab order, so tabbing lands on off-screen / clone
  // content and the browser scrolls it into view, shifting the track. Mirror
  // `inert` onto `aria-hidden` slides — it drops them from the tab order + AT tree
  // WITHOUT hiding them visually (so outgoing / wrap / swept-through slides still
  // render during the transform). A MutationObserver keeps it in sync across
  // navigation, autoplay, resize and clone re-rendering.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const syncInert = () => {
      root.querySelectorAll('.slick-slide').forEach((slide) => {
        if (slide.getAttribute('aria-hidden') === 'true') slide.setAttribute('inert', '');
        else slide.removeAttribute('inert');
      });
    };

    syncInert();
    // Watch only `aria-hidden` — the single attribute the inert sync keys off. react-slick
    // churns slide `class` on every animation frame; observing it too would fire this
    // callback (and a full re-sweep) needlessly many times per transition.
    const observer = new MutationObserver(syncInert);
    observer.observe(root, { subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });
    return () => observer.disconnect();
  }, [slidesCount]);

  const containerClassNames = classnames({ relative: arrows }, containerClassName);

  const arrowProps = { size: arrowSize, customClassName: arrowClassName, customStyles: arrowStyles } as ArrowProps;

  const responsiveSpaceBetween = useResponsiveValue(
    typeof spaceBetween === 'number' ? { base: spaceBetween } : spaceBetween,
  );

  const responsiveSlideWidth = useResponsiveValue(
    typeof slideWidth === 'number' ? { base: slideWidth } : (slideWidth ?? {}),
  );

  const responsiveSlidesToShow = useResponsiveValue(
    typeof slidesToShow === 'number' ? { base: slidesToShow } : (slidesToShow ?? {}),
  ) as number;

  return (
    <div ref={containerRef} className={containerClassNames}>
      <Slick
        beforeChange={(...args) => setCurrentSlide(args[1])}
        slidesToShow={responsiveSlidesToShow}
        swipeToSlide={swipeToSlide}
        infinite={infinite}
        arrows={arrows}
        prevArrow={
          <Arrow
            position="prev"
            variant={arrowVariant}
            {...arrowProps}
            customStyles={{
              ...arrowStyles,
              ...(responsiveSpaceBetween ? { left: `${Math.floor(responsiveSpaceBetween / 2)}px` } : {}),
            }}
            iconClassName={arrowIconClassName}
            overlayDarkArrow={overlayDarkArrow}
          />
        }
        nextArrow={
          <Arrow
            position="next"
            variant={arrowVariant}
            {...arrowProps}
            customStyles={{
              ...arrowStyles,
              ...(responsiveSpaceBetween ? { right: `${Math.floor(responsiveSpaceBetween / 2)}px` } : {}),
            }}
            iconClassName={arrowIconClassName}
            overlayDarkArrow={overlayDarkArrow}
          />
        }
        variableWidth={!!slideWidth}
        ref={ref}
        {...props}
      >
        {React.Children.map(children, (Child, index) => (
          <Slide
            slidesCount={slidesCount}
            index={index}
            spaceBetween={responsiveSpaceBetween}
            slideWidth={responsiveSlideWidth}
            slidesToShow={responsiveSlidesToShow}
            currentSlide={currentSlide}
          >
            {Child}
          </Slide>
        ))}
      </Slick>
    </div>
  );
};

export default Slider;
